/**
 * Competency transcript helpers.
 *
 * Expected API payload (getStudentCompetencyTranscriptByApplication):
 * {
 *   // Prefer student-specific credits for the current application student:
 *   student_credits?: string,
 *   member_credits?: string,
 *   member_role?: string,
 *   // Fallback only — full team dump; frontend will try to extract this student
 *   team_credits?: string,
 *   track_name?: string,
 *   // Full track rubric — every competency with scoring-item counts + scores
 *   competencies: [{
 *     competency_name: string,
 *     competency_id?: string,
 *     items?: [{ item_name?: string, score: number, full_score?: number }],
 *     item_count?: number,
 *     student_score?: number,
 *     full_score?: number
 *   }]
 * }
 *
 * Flat scoring rows are also accepted and grouped by competency:
 *   scoring_items / scores: [{ competency_name, competency_id, score, full_score, ... }]
 */

import { findMemberContribution, resolveDisplayMediaUrl } from './teamCredits'

export const TOP_COMPETENCY_COUNT = 7

function toNumber(value) {
    const n = Number(value)
    return isNaN(n) ? 0 : n
}

function normalizeNameKey(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fff]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
}

function contributionThemes(value) {
    const text = String(value || '').toLowerCase()
    if (!text) {
        return []
    }
    const rules = [
        { label: 'Research and problem framing', re: /research|survey|interview|persona|stakeholder|problem|need|pain point/ },
        { label: 'Evidence review and data analysis', re: /data|analysis|metric|evidence|impact|result|evaluation|testing|experiment/ },
        { label: 'Solution design and prototype development', re: /design|prototype|wireframe|solution|feature|model|build|develop|implementation/ },
        { label: 'Planning and team coordination', re: /plan|timeline|coordinate|leader|manage|schedule|meeting|collaborat|team/ },
        { label: 'Communication and final presentation', re: /present|presentation|report|writing|communicat|pitch|visual|slide|document/ }
    ]
    return rules.filter(rule => rule.re.test(text)).map(rule => rule.label)
}

function studentNameCandidates(studentInfo) {
    const info = studentInfo || {}
    const given = info.student_givenName_pinyin || info.student_givenName || info.givenName || ''
    const last = info.student_lastName_pinyin || info.student_lastName || info.lastName || ''
    const fullPinyin = [given, last].filter(Boolean).join(' ')
    const fullNative = [info.student_givenName, info.student_lastName].filter(Boolean).join(' ')
    const list = [
        fullPinyin,
        [last, given].filter(Boolean).join(' '),
        fullNative,
        info.student_name,
        info.name,
        given,
        last
    ]
    return list
        .map(normalizeNameKey)
        .filter((name, index, arr) => name && arr.indexOf(name) === index)
}

/**
 * Prefer student-specific credits. Never dump the entire team's Member Contributions
 * when we can identify the current student's section.
 */
export function resolveMemberCredits(payload, studentInfo) {
    const data = payload || {}

    // Prefer structured team_credits rows when backend/frontend provides them
    const structured = data.team_credits_members ||
        data.members ||
        data.member_list ||
        data.team_members ||
        []
    if (Array.isArray(structured) && structured.length && typeof structured[0] === 'object') {
        const fromStructured = findMemberContribution(structured, studentInfo)
        if (fromStructured.text) {
            return fromStructured
        }
    }

    const direct = data.student_credits ||
        data.member_credits ||
        data.member_contribution ||
        data.contribution_text ||
        data.my_credits ||
        ''
    if (direct && String(direct).trim()) {
        return {
            text: String(direct).trim(),
            role: data.member_role || data.role || data.application_team_role || '',
            source: 'student'
        }
    }

    const members = data.members || data.member_list || data.team_members || []
    if (members && members.length) {
        const names = studentNameCandidates(studentInfo)
        const matched = members.find(member => {
            const memberName = normalizeNameKey(
                member.name ||
                [member.student_givenName_pinyin, member.student_lastName_pinyin].filter(Boolean).join(' ') ||
                member.student_name ||
                member.member ||
                ''
            )
            return memberName && names.some(name => memberName === name || memberName.indexOf(name) >= 0 || name.indexOf(memberName) >= 0)
        })
        if (matched && (matched.credits || matched.contribution || matched.text)) {
            return {
                text: String(matched.credits || matched.contribution || matched.text).trim(),
                role: matched.role || matched.member_role || '',
                source: 'member_list',
                avatar: resolveDisplayMediaUrl(matched.avatar || matched.avatar_url || '')
            }
        }
    }

    const teamTextRaw = data.team_credits || data.teamCredits || data.contribution || ''
    if (Array.isArray(teamTextRaw) && teamTextRaw.length && typeof teamTextRaw[0] === 'object') {
        const fromRows = findMemberContribution(teamTextRaw, studentInfo)
        if (fromRows.text) {
            return fromRows
        }
        return { text: '', role: '', source: 'unmatched' }
    }
    const teamText = teamTextRaw
    if (!teamText) {
        return { text: '', role: '', source: 'empty' }
    }

    const extracted = extractMemberSectionFromTeamCredits(teamText, studentInfo)
    if (extracted.text) {
        return extracted
    }

    // Do not fall back to the full team dump — that belongs to other students.
    return { text: '', role: '', source: 'unmatched' }
}

// Start at 100 words. The document measures the rendered paragraph and requests
// a more concise paraphrase when needed; the original contribution is retained.
export const MEMBER_CREDITS_SAFE_WORD_LIMIT = 100
export function compressMemberCreditsForTranscript(value, maxWords = MEMBER_CREDITS_SAFE_WORD_LIMIT) {
    let source = String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    if (!source) return ''
    // Remove the repeated subject before matching activities (e.g. the surname
    // "Test" is not evidence that a student performed testing).
    const subject = source.match(/^(.{1,70}?)\s+(?:serves?|leads?|drives?|oversees?|coordinates?|manages?|contributes?|contributed|conducted)\b/i)
    if (subject && !/^I$|^We$|^The student$/i.test(subject[1])) {
        const escaped = subject[1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        source = source.replace(new RegExp('\\b' + escaped + '\\b', 'gi'), 'The student')
    }
    const text = source.toLowerCase()
    const sentences = []
    const list = items => items.length < 2 ? items[0] : items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1]
    const add = (lead, rules) => {
        const tasks = rules.filter(([re]) => re.test(text)).map(([, phrase]) => phrase)
        if (tasks.length) sentences.push(lead + ' ' + list(tasks) + '.')
    }
    const methods = []
    if (/\bscamper\b/i.test(source)) methods.push('SCAMPER')
    if (/\breverse brainstorming\b/i.test(source)) methods.push('reverse brainstorming')
    const count = '(\\d+|one|two|three|four|five|six|seven|eight|nine|ten|twelve|eighteen|twenty)'
    const ideas = text.match(new RegExp('\\b' + count + '\\s+(?:initial\\s+)?ideas\\b'))
    const alternatives = text.match(new RegExp('\\b' + count + '\\s+(?:distinct\\s+)?solution alternatives\\b'))
    if (methods.length) {
        const outcome = ideas && alternatives ? ' to develop ' + ideas[1] + ' ideas into ' + alternatives[1] + ' solution alternatives' : ' in the development of solution ideas'
        sentences.push('Used ' + list(methods) + outcome + '.')
    }
    if (/\b(?:team leader|coordinating|coordination|coordinated)\b/.test(text) && /\b(?:problem framing|project framing|goal definition)\b/.test(text)) {
        sentences.push('Coordinated the team’s work and helped define the project’s problem and goals.')
    }
    const milestones = text.match(new RegExp('\\b' + count + '\\s+(?:key\\s+)?milestones\\b'))
    if (milestones) {
        const practices = []
        if (/weekly (?:progress )?reviews/.test(text)) practices.push('weekly progress reviews')
        if (/meeting records/.test(text)) practices.push('meeting records')
        sentences.push('Organised the project around ' + milestones[1] + ' milestones' + (practices.length ? ', with ' + list(practices) : '') + '.')
    }
    add('Research contributions included', [
        [/\bsurveys?\b|\bquestionnaires?\b/, 'surveys'], [/\binterviews?\b/, 'interviews'],
        [/\buser research\b|\bpersonas?\b/, 'exploration of user needs'], [/\bliterature\b|\bdesk research\b/, 'review of published research']])
    add('Examined evidence through', [
        [/\bdata\b/, 'data analysis'], [/\bexperiments?\b|\btesting\b|\btested\b/, 'testing'],
        [/\bmetrics?\b|\bevaluat(?:ed|ion|ing)\b/, 'evaluation of findings']])
    add('Development work included', [
        [/\bprototypes?\b|\bprototyping\b/, 'prototype development'], [/\bwireframes?\b/, 'wireframes'],
        [/\b(?:solution design|designed|designing)\b/, 'solution design'], [/\bcode\b|\bcoding\b|\bprogramming\b|\bimplementation\b/, 'implementation']])
    if (!milestones && !sentences.some(sentence => sentence.startsWith('Coordinated'))) {
        add('Supported the team through', [[/\bcoordinat\w*\b|\bmanag\w*\b|\bleader\b/, 'coordination of shared work'], [/\btimeline\b|\bschedul\w*\b|\bplanning\b/, 'planning'], [/\bmeetings?\b/, 'team meetings']])
    }
    // Administrative uploads and submission checklists are not personal achievements.
    const contentText = text.replace(/[^.]*\b(?:final submission|uploaded|uploading)\b[^.]*\.?/g, '')
    const communication = []
    if (/\breports?\b|\bwriting\b/.test(contentText)) communication.push('written project materials')
    if (/\bpresentation\b|\bpresented\b|\bpitch\b|\bslides?\b/.test(contentText)) communication.push('presentation work')
    if (/\bvisuals?\b|\billustrations?\b/.test(contentText)) communication.push('visual communication')
    if (communication.length) sentences.push('Communicated the work through ' + list(communication) + '.')
    if (!sentences.length) return 'Contributed in the role described in the individual contribution statement.'
    const result = []
    sentences.forEach(sentence => {
        if ((result.join(' ') + ' ' + sentence).trim().split(/\s+/).length <= maxWords) result.push(sentence)
    })
    return result.join(' ')
}

/**
 * Team credits text is often one blob with sections starting as:
 *   "Yichen Jiao (Team Leader) Yichen serves as..."
 *   "A6 Test A6 Test actively contributed..."
 * Extract only the section that matches the current student.
 */
export function extractMemberSectionFromTeamCredits(teamCredits, studentInfo) {
    const text = String(teamCredits || '').trim()
    if (!text) {
        return { text: '', role: '', source: 'empty' }
    }

    const names = studentNameCandidates(studentInfo)
    if (!names.length) {
        return { text: '', role: '', source: 'unmatched' }
    }

    const sections = findTeamCreditSections(text)
    for (let i = 0; i < sections.length; i++) {
        const section = sections[i]
        const sectionName = normalizeNameKey(section.name)
        const hit = names.some(name => {
            return sectionName === name ||
                sectionName.indexOf(name) >= 0 ||
                name.indexOf(sectionName) >= 0
        })
        if (!hit) {
            continue
        }
        const end = i + 1 < sections.length ? sections[i + 1].start : text.length
        let body = text.slice(section.headerEnd, end).trim()
        // Drop leading duplicate "A6 Test" if authored as "Name Name body".
        const dup = new RegExp('^' + escapeRegExp(section.name) + '\\s+', 'i')
        body = body.replace(dup, '').trim()
        const combined = (section.name +
            (section.role ? ' (' + section.role + ')' : '') +
            (body ? ' ' + body : '')).trim()
        return {
            text: combined,
            role: section.role,
            source: 'extracted'
        }
    }

    // Soft match: student name → cut before next member header (supports A6/A7-style names).
    const lower = text.toLowerCase()
    for (let i = 0; i < names.length; i++) {
        const needle = names[i]
        const idx = lower.indexOf(needle)
        if (idx < 0) {
            continue
        }
        let end = text.length
        const after = text.slice(idx + needle.length)
        const nextHeader = after.search(
            /\s(?:[A-Z][A-Za-z0-9]+(?:\s+[A-Z][A-Za-z0-9.'-]+){0,3})\s+\1\b|\s[A-Z][A-Za-z0-9]+(?:\s+[A-Z][A-Za-z0-9.'-]+){1,3}\s*\(/
        )
        if (nextHeader >= 0) {
            end = idx + needle.length + nextHeader
        }
        return {
            text: text.slice(idx, end).trim(),
            role: '',
            source: 'extracted'
        }
    }

    return { text: '', role: '', source: 'unmatched' }
}

function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Prefer "Name Name ..." headers (incl. alphanumeric given names like A6 Test).
 */
function findTeamCreditSections(text) {
    const sections = []
    const dupNameRe = /(?:^|[\s.])([A-Z][A-Za-z0-9]+(?:\s+[A-Z][A-Za-z0-9.'-]+){0,3})\s+\1\b/g
    let match
    while ((match = dupNameRe.exec(text)) !== null) {
        const full = match[0]
        const leading = /^[\s.]/.test(full) ? 1 : 0
        const start = match.index + leading
        sections.push({
            name: match[1].trim(),
            role: '',
            start,
            headerEnd: match.index + full.length
        })
    }
    if (sections.length) {
        return sections.sort((a, b) => a.start - b.start)
    }

    const headerRe = /(?:^|\n)([A-Z][A-Za-z0-9]+(?:\s+[A-Z][A-Za-z0-9.'-]+){0,3})(?:\s*\(([^)]+)\))?\s+/g
    while ((match = headerRe.exec(text)) !== null) {
        sections.push({
            name: match[1].trim(),
            role: (match[2] || '').trim(),
            start: match.index + (match[0].startsWith('\n') ? 1 : 0),
            headerEnd: match.index + match[0].length
        })
    }
    return sections
}

function aggregateFromItems(name, competencyId, items) {
    const studentScore = items.reduce((sum, row) => sum + toNumber(row.score), 0)
    const fullScore = items.reduce((sum, row) => {
        if (row.full_score !== undefined && row.full_score !== null && row.full_score !== '') {
            return sum + toNumber(row.full_score)
        }
        if (row.fullScore !== undefined && row.fullScore !== null && row.fullScore !== '') {
            return sum + toNumber(row.fullScore)
        }
        return sum
    }, 0)

    return {
        name,
        competencyId,
        itemCount: items.length,
        studentScore,
        fullScore,
        scoreRatio: fullScore > 0 ? studentScore / fullScore : studentScore,
        // Item-level detail is kept for aggregation only; UI should not render item names.
        items: []
    }
}

/**
 * Aggregate track rubric competencies:
 * - how many scoring items each competency has
 * - how many points the student earned
 * Does not expose per-item names for display.
 */
export function aggregateCompetencies(competencies) {
    if (!competencies || !competencies.length) {
        return []
    }

    return competencies.map((item, index) => {
        const name = (item && (item.competency_name || item.name || item.competency)) || ('Competency ' + (index + 1))
        const competencyId = (item && (item.competency_id || item._id)) || ''
        const items = (item && item.items) || []

        if (items.length > 0) {
            return aggregateFromItems(name, competencyId, items)
        }

        const itemCount = toNumber(item.item_count || item.scoring_item_count)
        const studentScore = toNumber(item.student_score !== undefined ? item.student_score : item.score)
        const fullScore = toNumber(item.full_score)

        return {
            name,
            competencyId,
            itemCount,
            studentScore,
            fullScore,
            scoreRatio: fullScore > 0 ? studentScore / fullScore : studentScore,
            items: []
        }
    })
}

/** Group flat scoring rows by competency, then aggregate. */
export function aggregateFlatScoringItems(rows) {
    if (!rows || !rows.length) {
        return []
    }

    const groups = {}
    const order = []

    rows.forEach((row, index) => {
        const name = row.competency_name || row.competency || row.name || ('Competency ' + (index + 1))
        const competencyId = row.competency_id || row._id || name
        const key = String(competencyId || name)
        if (!groups[key]) {
            groups[key] = {
                name,
                competencyId,
                items: []
            }
            order.push(key)
        }
        groups[key].items.push({
            score: row.score !== undefined ? row.score : row.student_score,
            full_score: row.full_score !== undefined ? row.full_score : row.fullScore
        })
    })

    return order.map(key => {
        const group = groups[key]
        return aggregateFromItems(group.name, group.competencyId, group.items)
    })
}

/** Highest-scoring competencies first (student's strongest). */
export function sortCompetenciesByScore(list) {
    const ratio = row => row.fullScore > 0 ? row.studentScore / row.fullScore : Number(row.scoreRatio) || 0
    return (list || []).slice().sort((a, b) => ratio(b) - ratio(a) || String(a.name).localeCompare(String(b.name)))
}

/** A competency counts as strong only at >= 80% of its available marks. */
export const STRONG_COMPETENCY_MIN_RATIO = 0.8

/**
 * Strong competencies for the transcript's first page: among competencies scored at
 * >= 80%, the ones with the most marks actually earned, ordered by earned marks.
 * Ranking by percentage alone let marginal dimensions (e.g. 2 / 2) crowd out core ones.
 */
export function getTopCompetencies(competencies, count) {
    const limit = count || TOP_COMPETENCY_COUNT
    const ratio = row => row.fullScore > 0 ? row.studentScore / row.fullScore : Number(row.scoreRatio) || 0
    const earned = row => Number(row.studentScore) || 0
    return (competencies || [])
        .filter(row => ratio(row) >= STRONG_COMPETENCY_MIN_RATIO - 1e-9)
        .sort((a, b) => earned(b) - earned(a) || ratio(b) - ratio(a) || String(a.name).localeCompare(String(b.name)))
        .slice(0, limit)
}

export function buildCompetencyTranscriptView(payload, fallback) {
    const data = payload || {}
    const base = fallback || {}
    const studentInfo = base.studentInfo || {}

    let all = aggregateCompetencies(data.competencies || data.competency_list || [])
    if (!all.length) {
        all = aggregateFlatScoringItems(
            data.scoring_items || data.scores || data.score_items || data.rubric_scores || []
        )
    }

    const issued = data.credential_status === 'issued'
    const memberCredits = issued
        ? { text: data.published_member_credits || '', role: data.member_role || '', source: 'published' }
        : resolveMemberCredits(data, studentInfo)
    const projectInfo = base.projectInfo || {}

    return {
        memberCredits: issued ? memberCredits.text : compressMemberCreditsForTranscript(memberCredits.text),
        originalMemberCredits: memberCredits.text,
        memberRole: memberCredits.role,
        memberCreditsSource: memberCredits.source,
        // keep legacy key for callers still using teamCredits naming
        teamCredits: memberCredits.text,
        trackName: data.track_name || data.trackName ||
            projectInfo.project_product ||
            projectInfo.track_name ||
            projectInfo.project_ename ||
            '',
        totalStudentScore: toNumber(
            data.total_student_score !== undefined ? data.total_student_score : data.totalStudentScore
        ),
        totalFullScore: toNumber(
            data.total_full_score !== undefined ? data.total_full_score : data.totalFullScore
        ),
        totalItemCount: toNumber(
            data.total_item_count !== undefined ? data.total_item_count : data.totalItemCount
        ),
        allCompetencies: all,
        topCompetencies: getTopCompetencies(all, TOP_COMPETENCY_COUNT)
    }
}

export function formatCompetencyScore(row) {
    if (!row) {
        return '-'
    }
    if (row.fullScore > 0) {
        return row.studentScore + ' / ' + row.fullScore
    }
    return String(row.studentScore)
}
